import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Row, Col, Avatar, Layout } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { getUserProfile } from '../../Services/graphService';
import { ProfileCard } from '../Atoms/ProfileCard';
import { BannerSlide } from '../Atoms/BannerSlide';
import Slideshow from '../Molecules/Slideshow';
import { fetchWithRetry } from '../utils/fetchWithRetry';

// Import gallery images
import GallaryImage6 from '../../assets/images/Images/GallaryImage9.jpg';
import GallaryImage1 from '../../assets/images/Images/GallaryImage10.jpg';
import GallaryImage2 from '../../assets/images/Images/GallaryImage11.jpg';
import GallaryImage4 from '../../assets/images/Images/GallaryImage12.jpg';
import GallaryImage5 from '../../assets/images/Images/GallaryImage13.jpg';

interface UserProfile {
  displayImage: string | null;
  jobTitle: string | null;
  officeLocation: string | null;
  displayName: string | null;
}

// Original MediaItem interface from BannerSection
interface LocalMediaItem {
  mediaSrc: string | File; 
  mediaType: 'IMAGE' | 'VIDEO';
  description: string;
}

// Memoized function to convert local media items to the format expected by Slideshow
const convertMediaItems = (items: LocalMediaItem[]) => {
  return items.map((item, index) => ({
    ...item,
    id: `media-${index}`, // Add required id property
    name: item.description, // Add required name property based on description
    caption: item.description // Add caption based on description
  }));
};

// Configuration for caching and performance
const CONFIG = {
  cacheExpiry: 15 * 60 * 1000, // 15 minutes cache
  requestTimeout: 30000 // 30 second timeout
};

export const BannerSection: React.FC = React.memo(() => {
  // Memoized SharePoint configuration
  const sharePointConfig = useMemo(() => ({
    siteId: 'apteanonline.sharepoint.com,0e3f4e44-49c2-4c09-be9b-c78ce324308f,ee070650-20b0-4c36-a167-32622bea5a22',
    driveId: 'b!RE4_DsJJCUy-m8eM4yQwj1AGB-6wIDZMoWcyYivqWiILIwCLC7Z9S5p5YVdvX5xh',
    folderPath: '/Thanks - Pictures'
  }), []);

  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [nominatorEmail, setNominatorEmail] = useState('');
  const [nominatorName, setNominatorName] = useState('');
  const [mediaItems, setMediaItems] = useState<LocalMediaItem[]>([]);
  const [isLoadingProfile, setIsLoadingProfile] = useState(true);
  const [profileError, setProfileError] = useState<string | null>(null);
  const { instance, accounts } = useMsal();

  // Memoize API URL to prevent recreation
  const API_URL = useMemo(() => process.env.REACT_APP_API_URL, []);

  // Memoized fallback media items to prevent recreation
  const fallbackMediaItems = useMemo(() => [
    {
      mediaSrc: GallaryImage1.toString(),
      mediaType: 'IMAGE' as const,
      description: '',
    },
    {
      mediaSrc: GallaryImage2.toString(),
      mediaType: 'IMAGE' as const,
      description: '',
    },
    {
      mediaSrc: GallaryImage4.toString(),
      mediaType: 'IMAGE' as const,
      description: '',
    },
    {
      mediaSrc: GallaryImage5.toString(),
      mediaType: 'IMAGE' as const,
      description: '',
    },
    {
      mediaSrc: GallaryImage6.toString(),
      mediaType: 'IMAGE' as const,
      description: '',
    },
  ], []);

  // Removed callMsGraph - now using centralized service

  const fetchToken = useCallback(async () => {
    try {
      if (!accounts[0]) throw new Error('No account found');
      
      const res = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0]
      });
      return res.idToken;
    } catch (error) {
      console.error('Token fetch failed:', error);
      throw error;
    }
  }, [instance, accounts]);

  const requestProfileData = useCallback(async () => {
    try {
      setIsLoadingProfile(true);
      setProfileError(null);
      
      const profile = await getUserProfile(instance, 'https://login.microsoftonline.com/560ec2b0-df0c-4e8c-9848-a15718863bb6', true);
      
      setNominatorName(profile.displayName || 'Default Nominator');
      setNominatorEmail(profile.mail?.toLowerCase() || '');
    } catch (error) {
      console.error("Profile data request failed:", error);
      setProfileError('Failed to load profile data');
      
      // Try to use stale cache as fallback
      const staleCache = localStorage.getItem('userProfileData');
      if (staleCache) {
        const parsedProfile = JSON.parse(staleCache);
        setNominatorName(parsedProfile.displayName || 'Default Nominator');
        setNominatorEmail(parsedProfile.mail?.toLowerCase() || '');
      } else {
        instance.loginRedirect(loginRequest);
      }
    } finally {
      setIsLoadingProfile(false);
    }
  }, [instance]);

  useEffect(() => {
    requestProfileData();
    setInitialMediaItems();
  }, [requestProfileData]);

  useEffect(() => {
    fetchUserProfile(nominatorEmail);
  }, [nominatorEmail]);

  const fetchUserProfile = useCallback(async (userEmail: string) => {
    if (!userEmail) return;
    
    try {
      // Check cache first
      const cacheKey = `userProfile_${userEmail}`;
      const cachedProfile = localStorage.getItem(cacheKey);
      const cacheTimestamp = localStorage.getItem(`${cacheKey}_timestamp`);
      const now = Date.now();
      const isCacheValid = cacheTimestamp && (now - parseInt(cacheTimestamp)) < CONFIG.cacheExpiry;
      
      if (cachedProfile && isCacheValid) {
        setUserProfile(JSON.parse(cachedProfile));
        return;
      }
      
      const token = await fetchToken();
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), CONFIG.requestTimeout);
      
      const response = await fetchWithRetry(
        `${API_URL}/api/Users/GetProfileImageByEmail?email=${encodeURIComponent(userEmail)}`,
        {
          headers: {
            'Content-Type': 'application/json', 
            Authorization: `Bearer ${token}`
          },
          signal: controller.signal
        }
      );
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Error fetching user profile: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      // Cache the profile data
      localStorage.setItem(cacheKey, JSON.stringify(data));
      localStorage.setItem(`${cacheKey}_timestamp`, now.toString());
      
      setUserProfile(data);
    } catch (error) {
      console.error('Error fetching user profile:', error);
      
      // Try to use stale cache as fallback
      const cacheKey = `userProfile_${userEmail}`;
      const staleCache = localStorage.getItem(cacheKey);
      if (staleCache) {
        setUserProfile(JSON.parse(staleCache));
      }
    }
  }, [fetchToken, API_URL]);

  const setInitialMediaItems = useCallback(() => {
    const initialMediaItems: LocalMediaItem[] = [
      {
        mediaSrc: GallaryImage1,
        mediaType: 'IMAGE',
        description: '',
      },
      {
        mediaSrc: GallaryImage2,
        mediaType: 'IMAGE',
        description: '',
      },
      {
        mediaSrc: GallaryImage4,
        mediaType: 'IMAGE',
        description: '',
      },
      {
        mediaSrc: GallaryImage5,
        mediaType: 'IMAGE',
        description: '',
      },
    ];
    setMediaItems(initialMediaItems);
  }, []);

  const handleImageClick = useCallback((url: string) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }, []);

  // Memoize display values to prevent unnecessary re-renders
  const nameToDisplay = useMemo(() => userProfile?.displayName ?? '', [userProfile?.displayName]);
  const roleToDisplay = useMemo(() => userProfile?.jobTitle ?? '', [userProfile?.jobTitle]);
  
  const avatarElement = useMemo(() => 
    userProfile?.displayImage ? (
      <Avatar 
        src={`data:image/jpeg;base64,${userProfile.displayImage}`} 
        size={64}
        style={{ objectFit: 'cover' }}
      />
    ) : (
      <Avatar icon={<UserOutlined />} size={64} />
    ), [userProfile?.displayImage]
  );

  // Memoize converted media items to prevent recreation
  const convertedMediaItems = useMemo(() => convertMediaItems(fallbackMediaItems), [fallbackMediaItems]);

  return (
    <Layout>
      <Row 
        gutter={[16, 16]} 
        style={{ 
          margin: 0,
          minHeight: '220px'
        }}
      >
        {/* Profile Card - Takes full width on small screens, 25% on medium, 20% on large */}
        <Col xs={24} sm={24} md={8} lg={6} xl={5}>
          <div style={{ height: '100%' }}>
            <ProfileCard
              name={nameToDisplay}
              role={roleToDisplay}
              avatar={avatarElement}
            />
          </div>
        </Col>
        
        {/* Banner Slide - Takes full width on small screens, 50% on medium, 40% on large */}
        {/* <Col xs={24} sm={24} md={16} lg={10} xl={9}> */}
        <Col xs={24} sm={24} md={16} lg={12} xl={11.5}>
        <div style={{ height: '100%' }}>
            <BannerSlide />
          </div>
        </Col>
        
        {/* Slideshow/Video - Takes full width on small screens, 100% below others on small-medium, 40% on large */}
        <Col xs={24} sm={24} md={24} lg={6} xl={7}>
        <div style={{ height: '100%' }}>
            <Slideshow 
              mediaItems={convertedMediaItems} 
              siteId={sharePointConfig.siteId}
              driveId={sharePointConfig.driveId}
              folderPath={sharePointConfig.folderPath}
              onFolderClick={handleImageClick}
              autoRotateInterval={5000}
              pauseOnHover={true}
              recursive={true}
              maxImagesPerFolder={2}
            />
          </div>
        </Col>
      </Row>
    </Layout>
  );
});
